import { Directive, ElementRef, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { IScrollable } from './interface';
import { ScrollDispatcher } from './scroll-dispatcher';

/**
 * Sends an event when the directive's element is scrolled. Registers itself with the
 * ScrollDispatcher service to include itself as part of its collection of scrolling events that it
 * can be listened to through the service.
 */
@Directive({
  selector: '[rtScrollable], rt-scrollable',
  exportAs: 'rtScrollable',
  host: {
    '[style.overflow-x]': 'suppressScrollX && "hidden"',
  },
})
export class ScrollableDirective implements OnInit, OnDestroy, IScrollable {
  @Input('rtScrollableSuppressScrollX') public suppressScrollX: boolean = false;

  @Output() public reachEnd: Observable<Event>;

  private _elementScrolled = new Subject<Event>();
  private _cachedScrollObservable: Observable<Event> = this._elementScrolled.asObservable();
  private _scrollListener: Function | null;

  constructor(
    private _elementRef: ElementRef,
    private _scroll: ScrollDispatcher,
    private _renderer2: Renderer2,
  ) {
    this.reachEnd = this.bottom$;
  }

  public ngOnInit(): void {
    this._scrollListener = this._renderer2.listen(
      this.getElementRef().nativeElement,
      'scroll',
      (event: Event) => {
        this._elementScrolled.next(event);
      },
    );

    this._scroll.register(this);
  }

  public ngOnDestroy(): void {
    this._scroll.deregister(this);

    if (this._scrollListener) {
      this._scrollListener();
      this._scrollListener = null;
    }
  }

  /**
   * Returns observable that emits when a scroll event is fired on the host element.
   */
  public elementScrolled(): Observable<Event> {
    return this._cachedScrollObservable;
  }

  public get top$(): Observable<Event> {
    return this.elementScrolled().pipe(
      filter((evt: Event) => (<HTMLElement>evt.target).scrollTop === 0),
    );
  }

  public get intermediate$(): Observable<Event> {
    return this.elementScrolled().pipe(
      filter(event => {
        const target = <HTMLElement>event.target;
        return (
          target.scrollTop !== 0 && target.scrollHeight - target.scrollTop !== target.clientHeight
        );
      }),
    );
  }

  public get bottom$(): Observable<Event> {
    return this.elementScrolled().pipe(
      filter(event => {
        const target = <HTMLElement>event.target;
        return (
          target.scrollTop !== 0 && target.scrollHeight - target.scrollTop === target.clientHeight
        );
      }),
    );
  }

  public below$(limit: number): Observable<Event> {
    return this.elementScrolled().pipe(
      filter((evt: Event) => (<HTMLElement>evt.target).scrollTop > limit),
    );
  }

  public above$(limit: number): Observable<Event> {
    return this.elementScrolled().pipe(
      filter((evt: Event) => (<HTMLElement>evt.target).scrollTop < limit),
    );
  }

  public get x$(): Observable<number> {
    return this.elementScrolled().pipe(map(evt => (<HTMLElement>evt.target).scrollLeft));
  }

  public get y$(): Observable<number> {
    return this.elementScrolled().pipe(map(evt => (<HTMLElement>evt.target).scrollTop));
  }

  public getElementRef(): ElementRef {
    return this._elementRef;
  }
}
